import { BankOperationStatusesNew } from 'constants/bankOperationStatusesNew';
import { CommissionStatuses } from 'constants/commissionStatuses';

import { AxiosResponse, AxiosError } from 'axios';
import { axios, axiosMultipart } from 'config/axios';
import {
  BankOperation,
  BankOperationComment,
  ImportFile,
  ImportRow,
} from 'types/embedded/bankOperation';
import { ErrorData } from 'types/embedded/error';

export const bulkDeleteBankOperation = async (ids: number[]): Promise<AxiosResponse> => {
  return axios
    .delete('/bank_operations/bulk/delete', { data: { ids } })
    .then((response: AxiosResponse) => response?.data)
    .catch((error: AxiosError<{ data: ErrorData }>) => Promise.reject(error.response?.data));
};

export const bulkOnApprovalBankOperation = async (ids: number[]): Promise<AxiosResponse> => {
  return axios
    .patch(
      '/bank_operations/bulk/on_approval',
      { ids },
      {
        headers: {
          'content-type': 'application/merge-patch+json',
        },
      },
    )
    .then((response: AxiosResponse) => response?.data)
    .catch((error: AxiosError<{ data: ErrorData }>) => Promise.reject(error.response?.data));
};

export const bulkApproveBankOperation = async (ids: number[]): Promise<AxiosResponse> => {
  return axios
    .patch(
      '/bank_operations/bulk/approve',
      { ids },
      {
        headers: {
          'content-type': 'application/merge-patch+json',
        },
      },
    )
    .then((response: AxiosResponse) => response?.data)
    .catch((error: AxiosError<{ data: ErrorData }>) => Promise.reject(error.response?.data));
};

export const bulkRejectBankOperation = async (
  ids: number[],
  comment: string,
): Promise<AxiosResponse> => {
  return axios
    .patch(
      '/bank_operations/bulk/reject',
      { ids, comment },
      {
        headers: {
          'content-type': 'application/merge-patch+json',
        },
      },
    )
    .then((response: AxiosResponse) => response?.data)
    .catch((error: AxiosError<{ data: ErrorData }>) => Promise.reject(error.response?.data));
};

export const getBankOperationComments = async (id: number): Promise<BankOperationComment[]> => {
  return axios
    .get(`bank_operations/${id}/comments`, {
      headers: { Accept: 'application/ld+json' },
    })
    .then((response: AxiosResponse) => response.data['hydra:member'])
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const getBankOperationsAvailableActions = async (
  id: number,
): Promise<BankOperationStatusesNew[]> => {
  return axios
    .get(`bank_operations/${id}/available`)
    .then((response: AxiosResponse) => response.data.available)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export type UpdateBankOperationsStatusParams = {
  status: BankOperationStatusesNew;
  comment?: string;
};

export const updateBankOperationStatus = async (
  id: string,
  data: UpdateBankOperationsStatusParams,
): Promise<CommissionStatuses[]> => {
  return axios
    .patch(`bank_operations/${id}/status`, data, {
      headers: {
        'content-type': 'application/merge-patch+json',
      },
    })
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export type UpdateBankOperationsCommentParams = {
  description: string;
};

export const updateBankOperationComment = async (
  id: string,
  data: UpdateBankOperationsCommentParams,
): Promise<CommissionStatuses[]> => {
  return axios
    .patch(`bank_operations/${id}/description`, data, {
      headers: {
        'content-type': 'application/merge-patch+json',
      },
    })
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const createRelatedBankOperation = async (data: {
  data: [BankOperation, BankOperation];
}): Promise<BankOperation> => {
  return axios
    .post('bank_operation/related_operations', data)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const updateRelatedBankOperation = async (data: {
  data: [BankOperation, BankOperation];
}): Promise<BankOperation> => {
  return axios
    .put('bank_operation/related_operations', data)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const importFiles = async (file: FormData): Promise<AxiosResponse> => {
  return axiosMultipart
    .post('import_files', file)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const getActiveImportId = async (): Promise<{ id: string; message?: string } | null> => {
  return axios
    .get('import_file/active', {
      headers: { Accept: 'application/ld+json' },
    })
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => {
      if (error.response?.status === 404) {
        return null;
      }
      return Promise.reject(error.message);
    });
};

export const getActiveImportById = async (id: string): Promise<ImportFile> => {
  return axios
    .get(`import_files/${id}`, {
      headers: { Accept: 'application/ld+json' },
    })
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.message));
};

export const getActiveImportInvalid = async (id: string): Promise<ImportRow[]> => {
  return axios
    .get(`import_files/${id}/import_rows?status=invalid`, {
      headers: { Accept: 'application/ld+json' },
    })
    .then((response: AxiosResponse) => response.data['hydra:member'])
    .catch((error: AxiosError) => Promise.reject(error.message));
};

export const approveImport = async (id: string): Promise<Record<any, any>> => {
  return axios
    .post(`import_files/${id}/approve`, {})
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const rejectImport = async (id: string): Promise<Record<any, any>> => {
  return axios
    .post(`import_files/${id}/reject`, {})
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};
